import { type AnalyticsEvent } from '@autone/analytics';
import { useMemo, useState } from 'react';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import {
  type GetCoreProductGroupsRequest,
  useGetCoreProductGroupsQuery,
} from '../../../../redux/services';
import { TailwindFormField } from '../../../../tailwind';
import { MultiSelectDropdown } from '../../../multi-select-dropdown';
import { useMultiselectApiFilters } from '../../hooks';
import { type GetMultiselectMapper } from '../../types';
import { CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT } from '../../utilities';
import { getRelevantCoreProductFilterKeys } from '../utilities/core-product-filter-keys';

type ProductGroupsFilters = GetCoreProductGroupsRequest['body']['filters'];
interface Props<T extends FieldValues> {
  disabled?: boolean;
  control: Control<T>;
  name: Path<T>;
  catalogueMap?: GetMultiselectMapper<ProductGroupsFilters['catalogue'], T>;
  productMap?: GetMultiselectMapper<ProductGroupsFilters['product'], T>;
  locationClustersMap?: GetMultiselectMapper<
    ProductGroupsFilters['location_cluster'],
    T
  >;
  analyticsEvent?: AnalyticsEvent;
}

export const CoreProductGroupsMultiselect = <T extends FieldValues>({
  disabled,
  control,
  name,
  catalogueMap,
  productMap,
  locationClustersMap,
  analyticsEvent,
}: Props<T>) => {
  const catalogueFilters = useMultiselectApiFilters({
    control,
    map: catalogueMap,
    relevantFilterKeys: ['seasons', 'events'],
  });

  const productFilters = useMultiselectApiFilters({
    control,
    map: productMap,
    relevantFilterKeys: getRelevantCoreProductFilterKeys([]),
  });

  const locationClustersFilters = useMultiselectApiFilters({
    control,
    map: locationClustersMap,
    relevantFilterKeys: ['location_clusters'],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const { data, isLoading } = useGetCoreProductGroupsQuery({
    query: {
      limit: CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT,
    },
    body: {
      filters: {
        product: productFilters,
        catalogue: catalogueFilters,
        product_groups: {},
        location_cluster: locationClustersFilters,
        size_run: {},
        size: {},
      },
    },
  });

  const options = useMemo(
    () =>
      data?.items
        .map(({ group }) => group)
        .filter((group) =>
          group.toLowerCase().includes(searchTerm.toLowerCase()),
        ) ?? [],
    [data, searchTerm],
  );

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <MultiSelectDropdown
          disabled={disabled}
          className="h-fit"
          loading={isLoading}
          options={options}
          getOptionId={(option) => option}
          getOptionLabel={(option) => option}
          onSearchTermChange={setSearchTerm}
          selected={value ?? []}
          onSelectedChange={(ids) => onChange(ids)}
          analyticsEvent={analyticsEvent}
        />
      )}
    />
  );
};
